import { useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import {
  Copy,
  Download,
  FileSpreadsheet,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import Button from "../../../components/Buttons/Button";
import IconButton from "../../../components/Buttons/IconButton";
import TextField from "../../../components/Form/TextField";
import TableContainer from "../../../components/Tables/TableContainer";
import TableCell from "../../../pages/BillingAndPayments/components/TableCell";
import { useTranslationApp } from "../../../lib/i18next";
import {
  TABLE_COLOR_BORDER,
  TASK_ALERT_ERROR_MESSAGE_BG,
  TASK_ALERT_ERROR_MESSAGE_BORDER,
} from "../../../utils/colors";
import {
  TEMPLATES_PRODUCE_ADD_ROW,
  TEMPLATES_PRODUCE_COPY_TO_COLUMN,
  TEMPLATES_PRODUCE_DELETE_ROW,
  TEMPLATES_PRODUCE_DOWNLOAD_CONFIG,
  TEMPLATES_PRODUCE_DOWNLOAD_FORMAT,
  TEMPLATES_PRODUCE_LOAD_LAST_CONFIG,
  TEMPLATES_PRODUCE_ROW_COUNT,
  TEMPLATES_PRODUCE_ROW_COUNT_SINGLE,
  TEMPLATES_PRODUCE_ROWS,
  TEMPLATES_PRODUCE_ROWS_HINT,
  TEMPLATES_PRODUCE_UPLOAD_CSV,
  TEMPLATES_PRODUCE_VARIABLES_EYEBROW,
} from "../../../i18n/keysTranslations";
import ProduceImageCell from "./ProduceImageCell";
import { getProduceImageCellStatus } from "./produceRows";
import {
  produceCardSx,
  produceCardTitleSx,
  produceKickerSx,
  produceResizableMultilineSx,
} from "./produceCardStyles";

const optionalRules = { required: false };
const EMPTY_IMAGE_URLS = new Set();
const PRODUCE_COLUMN_MIN_WIDTH = 200;

const cellSx = {
  whiteSpace: "normal",
  overflow: "visible",
  textOverflow: "unset",
};

const listEditableColumns = ({ columns = [] } = {}) =>
  columns.filter((column) => column.type !== "image");

const resolveAdjacentEditableCell = ({
  columns = [],
  rowIndex = 0,
  columnKey = "",
  rowCount = 0,
  reverse = false,
} = {}) => {
  const editableColumns = listEditableColumns({ columns });
  if (!editableColumns.length) return null;

  const columnIndex = editableColumns.findIndex(
    (column) => column.key === columnKey
  );
  if (columnIndex < 0) return null;

  let nextRowIndex = rowIndex;
  let nextColumnIndex = columnIndex + (reverse ? -1 : 1);

  if (nextColumnIndex >= editableColumns.length) {
    nextRowIndex += 1;
    nextColumnIndex = 0;
  } else if (nextColumnIndex < 0) {
    nextRowIndex -= 1;
    nextColumnIndex = editableColumns.length - 1;
  }

  if (nextRowIndex < 0 || nextRowIndex >= rowCount) return null;

  return {
    index: nextRowIndex,
    key: editableColumns[nextColumnIndex].key,
  };
};

const ProduceRowsTable = ({
  control,
  columns = [],
  fields = [],
  rows = [],
  invalidCellKeysByRow = {},
  onClearInvalidCell = () => {},
  disabled = false,
  onAddRow = () => {},
  onRemoveRow = () => {},
  onChangeImage = () => {},
  onCopyValueToColumn = () => {},
  onUploadCsv = () => {},
  onLoadLastConfig = null,
  onDownloadFormat = () => {},
  onDownloadConfig = null,
  rowRefs,
  checkingImageUrls = EMPTY_IMAGE_URLS,
  failedImageUrls = EMPTY_IMAGE_URLS,
}) => {
  const { t } = useTranslationApp();
  const [editingCell, setEditingCell] = useState(null);
  const tabbingToNextCellRef = useRef(false);
  const rowCount = fields.length;
  const columnsKey = useMemo(
    () => columns.map((column) => column.key).join("\u0001"),
    [columns]
  );

  const onPickCsv = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) onUploadCsv({ file });
  };

  return (
    <Box sx={produceCardSx}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 0.75,
            }}
          >
            <FileSpreadsheet size={14} />
            <Typography variant="caption" sx={produceKickerSx}>
              {t(TEMPLATES_PRODUCE_VARIABLES_EYEBROW)}
            </Typography>
          </Box>
          <Typography sx={produceCardTitleSx}>
            {t(TEMPLATES_PRODUCE_ROWS)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(TEMPLATES_PRODUCE_ROWS_HINT)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={disabled ? undefined : onDownloadFormat}
            disabled={disabled}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              mt: 0.4,
              p: 0,
              border: "none",
              background: "transparent",
              color: "text.secondary",
              fontFamily: "inherit",
              fontSize: "0.875rem",
              fontWeight: 700,
              lineHeight: 1.4,
              whiteSpace: "nowrap",
              cursor: disabled ? "default" : "pointer",
              opacity: disabled ? 0.5 : 1,
              "&:hover": disabled ? undefined : { color: "text.primary" },
            }}
          >
            <Download size={16} />
            <Box
              component="span"
              sx={{ textDecoration: "underline", textUnderlineOffset: "3px" }}
            >
              {t(TEMPLATES_PRODUCE_DOWNLOAD_FORMAT)}
            </Box>
          </Box>
          <Button
            color="secondary"
            type="button"
            startIcon={<Plus size={16} />}
            onClick={onAddRow}
            disabled={disabled}
            sx={{ minWidth: 120, height: 36 }}
          >
            {t(TEMPLATES_PRODUCE_ADD_ROW)}
          </Button>
          <Button
            color="secondary"
            component="label"
            startIcon={<Upload size={16} />}
            disabled={disabled}
            sx={{ minWidth: 120, height: 36 }}
          >
            {t(TEMPLATES_PRODUCE_UPLOAD_CSV)}
            <input
              type="file"
              accept=".csv"
              hidden
              disabled={disabled}
              onChange={onPickCsv}
            />
          </Button>
        </Box>
      </Box>

      <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
        <TableContainer
          key={columnsKey}
          keys={fields}
          hasPagination={false}
          showEmptyText={false}
          elevation={0}
          rounded={false}
          sx={{
            borderRadius: "0px !important",
            boxShadow: "none",
            overflowX: "auto",
            overflowY: "hidden",
          }}
          sxContainer={{
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            borderRadius: "8px !important",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
            overflow: "hidden",
            "& .MuiTableHead-root .MuiTableCell-root": {
              borderBottom: "1px solid",
              borderColor: "divider",
            },
            "& .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root": {
              borderBottom: "1px solid",
              borderColor: TABLE_COLOR_BORDER,
            },
            "& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root": {
              filter: "brightness(0.98)",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                isHeader
                resizable={false}
                sx={{ minWidth: 48, width: 48, pl: "16px !important" }}
              >
                #
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  isHeader
                  sx={{ minWidth: PRODUCE_COLUMN_MIN_WIDTH }}
                >
                  {column.label}
                </TableCell>
              ))}
              <TableCell
                isHeader
                resizable={false}
                sx={{ minWidth: 56, width: 56 }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field, index) => {
              const isLastRow = index === fields.length - 1;
              const invalidKeys = new Set(invalidCellKeysByRow[index] || []);
              const rowValues = rows[index] || {};
              return (
                <TableRow
                  key={field.id}
                  ref={(node) => {
                    if (!rowRefs?.current) return;
                    rowRefs.current[index] = node;
                  }}
                  data-produce-row={index}
                >
                  <TableCell
                    indexRow={index}
                    isLastRow={isLastRow}
                    sx={{ minWidth: 48, pl: "16px !important" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {index + 1}
                    </Typography>
                  </TableCell>
                  {columns.map((column) => {
                    const isImage = column.type === "image";
                    const isInvalidCell = invalidKeys.has(column.key);
                    const isEditing =
                      editingCell?.index === index &&
                      editingCell?.key === column.key;
                    const cellName = `rows.${index}.${column.key}`;
                    return (
                      <TableCell
                        key={`${field.id}-${column.key}`}
                        indexRow={index}
                        isLastRow={isLastRow}
                        customColor={
                          isInvalidCell ? TASK_ALERT_ERROR_MESSAGE_BG : ""
                        }
                        sx={{
                          ...(isImage ? cellSx : {}),
                          minWidth: PRODUCE_COLUMN_MIN_WIDTH,
                          ...(isInvalidCell
                            ? {
                                boxShadow: `inset 0 0 0 2px ${TASK_ALERT_ERROR_MESSAGE_BORDER}`,
                              }
                            : {}),
                        }}
                        onClick={() => {
                          if (disabled || isImage) return;
                          setEditingCell({ index, key: column.key });
                          onClearInvalidCell({ index, key: column.key });
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              flex: isEditing ? 1 : "0 1 auto",
                              minWidth: 0,
                            }}
                          >
                            {isImage ? (
                              <ProduceImageCell
                                url={rowValues[column.key] || ""}
                                status={getProduceImageCellStatus({
                                  url: rowValues[column.key] || "",
                                  checkingUrls: checkingImageUrls,
                                  failedUrls: failedImageUrls,
                                })}
                                disabled={disabled}
                                onChange={(nextUrl) =>
                                  onChangeImage({
                                    index,
                                    key: column.key,
                                    nextUrl,
                                  })
                                }
                              />
                            ) : isEditing ? (
                              <TextField
                                control={control}
                                name={cellName}
                                rules={optionalRules}
                                variant="filled"
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                autoFocus
                                placeholder={String(
                                  column.originExample || ""
                                ).trim()}
                                actionsChange={() =>
                                  onClearInvalidCell({ index, key: column.key })
                                }
                                actionsBlur={() => {
                                  if (tabbingToNextCellRef.current) {
                                    tabbingToNextCellRef.current = false;
                                    return;
                                  }
                                  setEditingCell(null);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Tab") {
                                    const nextCell =
                                      resolveAdjacentEditableCell({
                                        columns,
                                        rowIndex: index,
                                        columnKey: column.key,
                                        rowCount,
                                        reverse: event.shiftKey,
                                      });
                                    if (!nextCell) return;
                                    event.preventDefault();
                                    tabbingToNextCellRef.current = true;
                                    setEditingCell(nextCell);
                                    onClearInvalidCell({
                                      index: nextCell.index,
                                      key: nextCell.key,
                                    });
                                  }
                                }}
                                sx={{
                                  minWidth: 140,
                                  ...produceResizableMultilineSx,
                                }}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                  color: rowValues[column.key]
                                    ? "text.primary"
                                    : "text.disabled",
                                }}
                              >
                                {rowValues[column.key] || "—"}
                              </Typography>
                            )}
                          </Box>
                          {rowCount > 1 &&
                          rowValues[column.key] &&
                          !isEditing ? (
                            <IconButton
                              tooltipText={TEMPLATES_PRODUCE_COPY_TO_COLUMN}
                              disabled={disabled}
                              tabIndex={-1}
                              onClick={(event) => {
                                event.stopPropagation();
                                onCopyValueToColumn({
                                  index,
                                  key: column.key,
                                });
                              }}
                              sx={{
                                width: 30,
                                height: 30,
                                flexShrink: 0,
                                color: "text.primary",
                                "&:hover": { color: "text.primary" },
                              }}
                            >
                              <Copy size={16} />
                            </IconButton>
                          ) : null}
                        </Box>
                      </TableCell>
                    );
                  })}
                  <TableCell
                    indexRow={index}
                    isLastRow={isLastRow}
                    sx={{ minWidth: 56 }}
                  >
                    <IconButton
                      tooltipText={TEMPLATES_PRODUCE_DELETE_ROW}
                      disabled={disabled}
                      tabIndex={-1}
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemoveRow({ index });
                      }}
                      sx={{
                        width: 32,
                        height: 32,
                        color: "text.primary",
                        "&:hover": { color: "text.primary" },
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </TableContainer>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          pt: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        {onDownloadConfig && rowCount > 0 ? (
          <Button
            color="secondary"
            type="button"
            startIcon={<Download size={16} />}
            onClick={onDownloadConfig}
            sx={{ minWidth: 120, height: 36 }}
          >
            {t(TEMPLATES_PRODUCE_DOWNLOAD_CONFIG)}
          </Button>
        ) : null}
        {onLoadLastConfig ? (
          <Typography
            variant="body2"
            color="text.secondary"
            component="button"
            type="button"
            onClick={onLoadLastConfig}
            sx={{
              p: 0,
              border: "none",
              background: "transparent",
              fontFamily: "inherit",
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              cursor: "pointer",
              "&:hover": { color: "text.primary" },
            }}
          >
            {t(TEMPLATES_PRODUCE_LOAD_LAST_CONFIG)}
          </Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
          {t(
            rowCount === 1
              ? TEMPLATES_PRODUCE_ROW_COUNT_SINGLE
              : TEMPLATES_PRODUCE_ROW_COUNT,
            { count: rowCount }
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default ProduceRowsTable;
